import { useCallback, useLayoutEffect } from "react";

import { FormStatus } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { FieldErrors, useFormState, UseFormReturn } from "react-hook-form";

import { AmplitudeEvents } from "../../../../common/constants/amplitude";

import type { AnyStepFormData } from "./AnyStepFormData";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormWithUserOrOrgAccessOutput, TicketingOutput } from "@/common/types/trpc";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";

interface SaveError {
  code: string;
  message: string;
  /** Set when the step already told the user what to fix, so the generic error snackbar would only add noise. */
  silent?: boolean;
}

interface BaseFormSaveAndNavigateConfig<TFormData extends AnyStepFormData> {
  form: UseFormReturn<TFormData>;
  errorMessage?: string;
  alwaysSaveOnNext?: boolean;
  alwaysSaveOnBack?: boolean;
  onFirstNextCompleted?: () => void;
  getSubmitTrackingProperties?: (formData: TFormData) => Record<string, unknown>;
  onInvalid?: (errors: FieldErrors<TFormData>) => void;
}

interface TicketingFormSaveAndNavigateConfig<
  TFormData extends AnyStepFormData,
> extends BaseFormSaveAndNavigateConfig<TFormData> {
  type: "ticketing";
  saveData: (formData: TFormData) => Promise<{ data: TicketingOutput | null; error: SaveError | null }>;
}

interface DonationFormSaveAndNavigateConfig<
  TFormData extends AnyStepFormData,
> extends BaseFormSaveAndNavigateConfig<TFormData> {
  type: "donation-form";
  saveData: (
    formData: TFormData
  ) => Promise<{ data: Partial<DonationFormWithUserOrOrgAccessOutput> | null; error: SaveError | null }>;
}

export type FormSaveAndNavigateConfig<TFormData extends AnyStepFormData> =
  | TicketingFormSaveAndNavigateConfig<TFormData>
  | DonationFormSaveAndNavigateConfig<TFormData>;

export function useFormSaveAndNavigate<TFormData extends AnyStepFormData>(
  config: FormSaveAndNavigateConfig<TFormData>
) {
  const { form, saveData, errorMessage, alwaysSaveOnNext, alwaysSaveOnBack, getSubmitTrackingProperties, onInvalid } =
    config;
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const {
    goToNextStep,
    goToPreviousStep,
    updateApiFormObject,
    saveCurrentStep,
    setIsStepChanging,
    setIsFormDirty,
    resetForm,
    currentStepIndex,
    currentStepKey,
    apiFormObject,
    stepProgress,
    markStepCompleted,
  } = useLiveFormEditorContext();

  const { isDirty } = useFormState(form);

  /* ---------------------------------------------
   * Save logic shared by both scenarios
   * ------------------------------------------- */
  const handleSave = useCallback(
    async (formData: TFormData, { showSuccessSnackbar = false }: { showSuccessSnackbar?: boolean } = {}) => {
      const { data, error } = await saveData(formData);

      if (error) {
        if (!error.silent) {
          enqueueSnackbar(errorMessage || t("common", "anErrorOccurredRetry"), {
            vibe: "danger",
          });
        }
        return false;
      }

      if (data) {
        updateApiFormObject(data);
      }

      // Reset form to keep field values but mark dirty = false
      form.reset({ ...form.getValues() });

      if (showSuccessSnackbar) {
        enqueueSnackbar(t("dashboard", "changesSaved"), {
          vibe: "positive",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
        });
      }

      return true;
    },
    [saveData, enqueueSnackbar, errorMessage, t, updateApiFormObject, form]
  );

  const handleSaveAndNext = form.handleSubmit(async (formData) => {
    logUserActionEvent(AmplitudeEvents.FormStepSubmitted, getSubmitTrackingProperties?.(formData));

    setIsStepChanging(true);
    if (form.formState.isDirty || isDirty || alwaysSaveOnNext) {
      await handleSave(formData);
    }

    // If the current step is the most advanced step, call the onFirstNextCompleted callback
    if (currentStepIndex === stepProgress.reached) {
      config.onFirstNextCompleted?.();
    }

    markStepCompleted(currentStepIndex, currentStepKey);
    goToNextStep();
    setIsStepChanging(false);
  }, onInvalid);

  const handleSaveAndBack = async () => {
    logUserActionEvent(AmplitudeEvents.FormBackButtonClicked);

    let validationErrors: FieldErrors<TFormData> = {};
    const isStepValid = await new Promise<boolean>((resolve) => {
      void form.handleSubmit(
        () => resolve(true),
        (errors) => {
          validationErrors = errors;
          resolve(false);
        }
      )();
    });

    const isDraft = apiFormObject.status === FormStatus.Draft;
    const isStepNeverCompleted = currentStepIndex > stepProgress.saved;
    const canLeaveInvalidStep = isDraft && isStepNeverCompleted;
    if (!isStepValid && !canLeaveInvalidStep) {
      onInvalid?.(validationErrors);
      return;
    }

    setIsStepChanging(true);
    if (isStepValid) {
      if (form.formState.isDirty || isDirty || alwaysSaveOnBack) {
        await handleSave(form.getValues());
      }
      markStepCompleted(currentStepIndex, currentStepKey);
    }
    goToPreviousStep();
    setIsStepChanging(false);
  };

  const handleSaveCurrentStep = useCallback(async (): Promise<boolean> => {
    logUserActionEvent(AmplitudeEvents.FormStepSubmitted, getSubmitTrackingProperties?.(form.getValues()));

    const isValid = await new Promise<boolean>((resolve) => {
      void form.handleSubmit(
        () => resolve(true),
        (errors) => {
          onInvalid?.(errors);
          resolve(false);
        }
      )();
    });
    if (!isValid) {
      return false;
    }

    if (!isDirty) {
      // Form is valid but not dirty, no need to save
      return true;
    }

    const wasSaved = await handleSave(form.getValues(), { showSuccessSnackbar: true });

    if (wasSaved) {
      markStepCompleted(currentStepIndex, currentStepKey);
    }

    return wasSaved;
  }, [
    logUserActionEvent,
    form,
    isDirty,
    handleSave,
    markStepCompleted,
    currentStepIndex,
    currentStepKey,
    getSubmitTrackingProperties,
    onInvalid,
  ]);

  useLayoutEffect(() => {
    saveCurrentStep.current = handleSaveCurrentStep;
  }, [handleSaveCurrentStep, saveCurrentStep]);

  // Let external code reset the form
  resetForm.current = () => form.reset();

  // useLayoutEffect ensures the dirty state is updated before paint,
  // so UI elements (like button labels) reflect the correct state immediately
  useLayoutEffect(() => {
    setIsFormDirty(isDirty);
  }, [isDirty, setIsFormDirty]);

  return {
    handleSaveAndNext,
    handleSaveAndBack,
    handleSave,
  };
}
